/**
 * Сообщения, которые живут в состоянии страницы до показа.
 *
 * В состоянии хранится ключ словаря с параметрами, а не переведённая строка:
 * перевод делается при рендере, и сообщение, показанное до смены языка,
 * после смены показывается на новом. Переведённая в момент set…() строка
 * так и осталась бы на прежнем языке.
 */

import i18n from '@/utils/i18n';

/** Сообщение из словаря. Свойство messageKey видит проверка ключей (tests/unit/i18n) */
export interface KeyMessage {
	messageKey: string;
	params?: Record<string, unknown>;
}

/** Готовый текст, пришедший из API: переводу он не подлежит */
export interface TextMessage {
	text: string;
}

export type DisplayMessage = KeyMessage | TextMessage;

/** Функция перевода в той мере, в какой она нужна сообщениям */
type Translate = (key: string, params?: Record<string, unknown>) => string;

/** Текст сообщения на текущем языке */
export function messageText(t: Translate, message: DisplayMessage): string {
	return 'messageKey' in message ? t(message.messageKey, message.params) : message.text;
}

/**
 * Ошибка слоя API, чьё сообщение переводится заново при показе.
 *
 * message -- текст на языке момента броска: его читают логи и те, кто не
 * знает про этот класс. Страница же берёт display и переводит его при
 * рендере. Ключ пишется с пространством имён: у слоя API своего нет.
 */
export class LocalizedError extends Error {
	readonly display: KeyMessage;

	constructor(display: KeyMessage) {
		super(i18n.t(display.messageKey, display.params));
		this.name = 'LocalizedError';
		this.display = display;
	}
}

/**
 * Сообщение об ошибке запроса: переводимое, если ошибка его несёт; текст
 * ошибки, если он есть; иначе сообщение по умолчанию.
 */
export function errorMessage(err: unknown, fallback: DisplayMessage): DisplayMessage {
	if (err instanceof LocalizedError) return err.display;
	/* ApiError, созданная самим клиентом (например, недоступность проверки сессии), несёт ключ словаря */
	if (err instanceof Error && 'display' in err && isKeyMessage(err.display)) return err.display;
	if (err instanceof Error && err.message) return { text: err.message };
	return fallback;
}

function isKeyMessage(value: unknown): value is KeyMessage {
	return (
		value != null &&
		typeof value === 'object' &&
		typeof (value as { messageKey?: unknown }).messageKey === 'string'
	);
}

/** Временный отказ сервиса или сети не предлагает исправлять введённые данные */
export const REQUEST_UNAVAILABLE: KeyMessage = { messageKey: 'common:errors.requestUnavailable' };

/** Общее сообщение об отказе действия, когда внятного текста на языке интерфейса нет */
export const GENERIC_ERROR: KeyMessage = { messageKey: 'common:errors.generic' };

/**
 * Текст похож на непереведённый: в нём есть латиница и нет ни одной кириллической буквы.
 * Так выглядят тексты исключений (Failed to fetch) и ответы сервера по умолчанию
 * (Access denied, Bad Gateway): человеку они ничего не говорят, а язык интерфейса
 * нарушают. Тексты сервера, написанные по-русски, под признак не попадают.
 */
export function isUntranslatedText(text: string): boolean {
	return /[A-Za-z]/.test(text) && !/[А-Яа-яЁё]/.test(text);
}

/**
 * Сообщение об отказе действия: то, что ошибка несёт переводимым или русским текстом.
 * Непереведённый текст исключения и общее сообщение, которым клиент API заменил такой
 * текст, уступают сообщению экрана (fallback): «Не удалось войти» точнее общего отказа.
 */
export function failureMessage(err: unknown, fallback: KeyMessage = GENERIC_ERROR): DisplayMessage {
	if (
		fallback === GENERIC_ERROR &&
		(err instanceof TypeError ||
			(err instanceof Error &&
				'status' in err &&
				typeof err.status === 'number' &&
				err.status >= 500 &&
				!(
					'display' in err &&
					isKeyMessage(err.display) &&
					err.display.messageKey !== GENERIC_ERROR.messageKey
				)))
	)
		return REQUEST_UNAVAILABLE;
	const message = errorMessage(err, fallback);
	if (message === GENERIC_ERROR) return fallback;
	if (message === REQUEST_UNAVAILABLE)
		return fallback === GENERIC_ERROR ? REQUEST_UNAVAILABLE : fallback;
	return 'text' in message && isUntranslatedText(message.text) ? fallback : message;
}
